/**
 * Approvals: hire, transfer, expense va bonus tasdiqlashlari bitta sahifada (eskiz 16).
 * Hire faqat tasdiqlangandan keyin hisobotga kiradi; rad etilsa driver tanlangan statusga qaytadi.
 */
import { rememberPageSize, urlPageSize } from "@/lib/pageSize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useSearchParams, useNavigate } from "react-router";
import { ApiError } from "@/lib/api";
import { cn, formatDate, formatWhen } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { can, type Me } from "@/features/auth/types";
import { useAppSettings } from "@/features/drivers/api";
import { useApprovalCounts, useApprovals, useDecideApproval, type DecisionInput } from "@/features/approvals/api";
import { RETURN_TO_OPTIONS, payloadString, type Approval, type ApprovalKind } from "@/features/approvals/types";
import { Badge, StatusPill } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Field, Input, NumberInput, Select, Textarea } from "@/components/ui/field";
import { EmptyState, ErrorState, PageHeader, PageLoading } from "@/components/ui/misc";
import { Modal } from "@/components/ui/modal";
import { Tabs } from "@/components/ui/tabs";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { ContextMenu, type MenuEntry } from "@/components/ui/context-menu";
import { callPhone, copyText, openInNewTab } from "@/lib/clipboard";
import { approvalMenu } from "@/features/approvals/menu";
import { PersonLink } from "@/components/PersonLink";
import { usePersonNav } from "@/features/people/usePersonNav";

const KINDS: ApprovalKind[] = ["hire", "transfer", "expense", "bonus"];
const CARRIER_NO_CONFIRM = "Carrier did not confirm start";

export function ApprovalsPage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Approvals me={me.data} />;
}

function Approvals({ me }: { me: Me }) {
  const { t } = useTranslation("approvals");
  const [params, setParams] = useSearchParams();
  const kindParam = params.get("kind");
  const kind: ApprovalKind = KINDS.includes(kindParam as ApprovalKind) ? (kindParam as ApprovalKind) : "hire";
  const history = params.get("history") === "1";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const pageSize = urlPageSize(params.get("page_size"), "approvals");

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const counts = useApprovalCounts();
  const list = useApprovals({ kind, status: history ? undefined : "pending", page, page_size: pageSize });
  // "Recently decided": oxirgi qarorlar (pending'larni chiqarib tashlaymiz)
  const recent = useApprovals({ kind, page_size: 12 }, !history);
  const decide = useDecideApproval();
  const settings = useAppSettings();

  const [rejecting, setRejecting] = useState<Approval | null>(null);
  const navigate = useNavigate();
  const { t: tm } = useTranslation("menu");
  const personNav = usePersonNav();
  const menuFor = (a: Approval): MenuEntry[] =>
    approvalMenu(
      a,
      {
        approve: () => setApproving(a),
        reject: () => setRejecting(a),
        navigate: (path) => navigate(path),
        openNewTab: openInNewTab,
        call: callPhone,
        copy: (text) => void copyText(text),
        profile: personNav,
        can: (perm) => can(me, perm),
      },
      tm,
    );
  const [approving, setApproving] = useState<Approval | null>(null);

  const rows = useMemo(() => {
    const results = list.data?.results ?? [];
    return history ? results.filter((a) => a.status !== "pending") : results;
  }, [list.data, history]);
  const recentDecided = useMemo(() => (recent.data?.results ?? []).filter((a) => a.status !== "pending").slice(0, 5), [recent.data]);

  const c = counts.data;
  const tabs = KINDS.map((k) => ({
    key: k,
    label: `${t(`kinds.${k}`)}${c ? ` (${c[k]})` : ""}`,
    tone: k === "expense" || k === "bonus" ? ("budget" as const) : undefined,
  }));

  const total = c?.total ?? 0;
  // Doira yorlig'i: bir nechta filial boshqaruvchisi hammasining nomini ko'radi (2026-10-18)
  const scope =
    me.role === "owner"
      ? t("scope.all")
      : me.scope_branches.length > 1
        ? me.scope_branches.map((b) => b.name).join(", ")
        : (me.branch?.name ?? t("scope.myBranch"));

  async function runDecision(input: DecisionInput): Promise<ApiError | null> {
    try {
      const res = await decide.mutateAsync(input);
      const name = res.driver_info?.full_name ?? res.summary ?? t(`kind.${res.kind}`);
      toast.success(input.action === "approve" ? t("toast.approved", { name }) : t("toast.rejected", { name }));
      return null;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isConflict || err.code === "already_decided") {
          toast.error(t("toast.alreadyDecided"), t("toast.listRefreshed"));
          return null;
        }
        return err;
      }
      toast.error(t("toast.saveFailed"), t("toast.checkConnection"));
      return null;
    }
  }

  const rejectReasons = useMemo(() => {
    const base = settings.data?.rejection_reasons ?? [];
    return base.includes(CARRIER_NO_CONFIRM) ? base : [...base, CARRIER_NO_CONFIRM];
  }, [settings.data]);

  return (
    <div>
      <PageHeader
        title={t("title")}
        subtitle={t("subtitle", { scope, count: total })}
        actions={
          <Button variant={history ? "primary" : "secondary"} onClick={() => setParam("history", history ? "" : "1")}>
            {history ? t("backToPending") : t("history")}
          </Button>
        }
      >
        <Tabs className="mt-3" items={tabs} value={kind} onChange={(k) => setParam("kind", k)} />
      </PageHeader>

      {kind === "hire" && !history && (
        <div className="mb-3 rounded-card border border-primary-soft bg-primary-soft/60 px-4 py-2 text-[12px]">
          {t("hireInfo")}
        </div>
      )}

      {list.isError ? (
        <ErrorState message={list.error instanceof ApiError ? list.error.message : t("loadFailed")} onRetry={() => list.refetch()} />
      ) : list.isPending ? (
        <PageLoading />
      ) : rows.length === 0 ? (
        <EmptyState
          title={history ? t("empty.history") : t("empty.pending", { kind: t(`kinds.${kind}`).toLowerCase() })}
          description={history ? t("empty.historyDesc") : t("empty.pendingDesc")}
        />
      ) : (
        <ApprovalTable kind={kind} rows={rows} history={history} onApprove={setApproving} onReject={setRejecting} menu={menuFor} />
      )}

      {list.data && list.data.pages > 1 && (
        <Pagination
          page={page}
          pages={list.data.pages}
          count={list.data.count}
          pageSize={list.data.page_size}
          onPage={(p) => setParam("page", String(p))}
          onPageSize={(s) => {
            rememberPageSize("approvals", s);
            setParam("page_size", String(s));
          }} label={t("pagination")} />
      )}

      {!history && (
        <Card className="mt-5">
          <CardHeader title={t("recent.title")} subtitle={recentDecided.length === 0 ? t("recent.none") : undefined} />
          {recentDecided.length > 0 && (
            <CardBody className="space-y-2">
              {recentDecided.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-2 text-[13px]">
                  <span className="font-semibold">{a.driver_info?.full_name ?? a.summary ?? t(`kind.${a.kind}`)}</span>
                  {a.requested_by && <span className="text-muted">· {a.requested_by.display_name}</span>}
                  {a.kind === "hire" && payloadString(a.payload, "job") && <span className="text-muted">· {payloadString(a.payload, "job")}</span>}
                  <StatusPill status={a.status} />
                  <span className="text-muted">
                    {t("recent.by", { who: a.decided_by?.display_name ?? "—" })} · {formatWhen(a.decided_at)}
                    {a.decision_reason ? ` · ${a.decision_reason}` : ""}
                  </span>
                </div>
              ))}
            </CardBody>
          )}
        </Card>
      )}

      <ApproveModal approval={approving} busy={decide.isPending} onClose={() => setApproving(null)} onSubmit={async (note, fee) => {
        if (!approving) return;
        const err = await runDecision({ id: approving.id, action: "approve", note, version: approving.version, ...(fee.trim() ? { fee_amount: fee.trim() } : {}) });
        if (err) toast.error(err.message);
        setApproving(null);
      }} />
      <RejectModal
        approval={rejecting}
        reasons={rejectReasons}
        busy={decide.isPending}
        onClose={() => setRejecting(null)}
        onSubmit={async (input) => {
          if (!rejecting) return null;
          const err = await runDecision({ id: rejecting.id, action: "reject", version: rejecting.version, ...input });
          if (!err) setRejecting(null);
          return err;
        }}
      />
    </div>
  );
}

function ApprovalTable({ kind, rows, history, onApprove, onReject, menu }: { kind: ApprovalKind; rows: Approval[]; history: boolean; onApprove: (a: Approval) => void; onReject: (a: Approval) => void; menu: (a: Approval) => MenuEntry[] }) {
  const { t } = useTranslation("approvals");
  const actions = (a: Approval) =>
    a.status === "pending" ? (
      <div className="flex gap-2">
        <Button size="sm" variant="ink" onClick={() => onApprove(a)}>
          {t("common:actions.approve")}
        </Button>
        <Button size="sm" onClick={() => onReject(a)}>
          {t("common:actions.reject")}
        </Button>
      </div>
    ) : (
      <div className="text-[12px] text-muted">
        <StatusPill status={a.status} /> {a.decided_by ? t("recent.by", { who: a.decided_by.display_name }) : ""} · {formatWhen(a.decided_at)}
        {a.decision_reason && <div className="mt-0.5">{a.decision_reason}</div>}
      </div>
    );

  const driverCell = (a: Approval) =>
    a.driver_info ? (
      <div>
        <Link to={`/drivers/${a.driver_info.id}`} className="font-semibold hover:underline">
          {a.driver_info.full_name}
        </Link>
        <div className="text-[11px] text-muted">{a.driver_info.phone}</div>
      </div>
    ) : (
      <span className="text-muted">—</span>
    );

  const requesterCell = (a: Approval) => (
    <div>
      {a.requested_by ? <PersonLink id={a.requested_by.id} name={a.requested_by.display_name} /> : "—"}
      {a.driver_info?.team && <div className="text-[11px] text-muted">{a.driver_info.team}</div>}
    </div>
  );

  if (kind === "hire") {
    return (
      <Table>
        <thead>
          <tr>
            <Th>{t("columns.driver")}</Th>
            <Th>{t("columns.recruiter")}</Th>
            <Th>{t("columns.carrierJob")}</Th>
            <Th>{t("columns.startDate")}</Th>
            <Th>{t("columns.evidence")}</Th>
            <Th>{t("columns.submitted")}</Th>
            <Th>{history ? t("columns.decision") : t("columns.actions")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <ContextMenu key={a.id} items={() => menu(a)}>
              <Tr>
              <Td>{driverCell(a)}</Td>
              <Td>{requesterCell(a)}</Td>
              <Td>{payloadString(a.payload, "job") || a.driver_info?.current_job || "—"}</Td>
              <Td>{formatDate(payloadString(a.payload, "start_date") || a.driver_info?.start_date)}</Td>
              <Td className="max-w-[260px]">
                {a.evidence ? <span>{a.evidence}</span> : <span className="text-danger">{t("noConfirmation")}</span>}
              </Td>
              <Td className="whitespace-nowrap text-muted">{formatWhen(a.created_at)}</Td>
              <Td>{actions(a)}</Td>
            </Tr>
            </ContextMenu>
          ))}
        </tbody>
      </Table>
    );
  }

  if (kind === "transfer") {
    return (
      <Table>
        <thead>
          <tr>
            <Th>{t("columns.driver")}</Th>
            <Th>{t("columns.fromTo")}</Th>
            <Th>{t("columns.requestedBy")}</Th>
            <Th>{t("columns.note")}</Th>
            <Th>{t("columns.submitted")}</Th>
            <Th>{history ? t("columns.decision") : t("columns.actions")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <ContextMenu key={a.id} items={() => menu(a)}>
              <Tr>
              <Td>{driverCell(a)}</Td>
              <Td>
                <span className="text-muted">{payloadString(a.payload, "from_owner") || t("pool")}</span> → <span className="font-semibold">{payloadString(a.payload, "to_user")}</span>
              </Td>
              <Td>{requesterCell(a)}</Td>
              <Td className="max-w-[260px]">{a.evidence || <span className="text-muted">—</span>}</Td>
              <Td className="whitespace-nowrap text-muted">{formatWhen(a.created_at)}</Td>
              <Td>{actions(a)}</Td>
            </Tr>
            </ContextMenu>
          ))}
        </tbody>
      </Table>
    );
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>{t(`kind.${kind}`)}</Th>
          <Th>{t("columns.requestedBy")}</Th>
          <Th>{t("columns.branch")}</Th>
          <Th>{t("columns.note")}</Th>
          <Th>{t("columns.submitted")}</Th>
          <Th>{history ? t("columns.decision") : t("columns.actions")}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a) => (
          <ContextMenu key={a.id} items={() => menu(a)}>
            <Tr>
            <Td>
              <span className="font-semibold">{a.summary || `${t(`kind.${kind}`)} #${a.id}`}</span>
              {a.driver_info && <div className="text-[11px] text-muted">{a.driver_info.full_name}</div>}
            </Td>
            <Td>{requesterCell(a)}</Td>
            <Td>{a.branch_name ?? "—"}</Td>
            <Td className="max-w-[260px]">{a.evidence || <span className="text-muted">—</span>}</Td>
            <Td className="whitespace-nowrap text-muted">{formatWhen(a.created_at)}</Td>
            <Td>{actions(a)}</Td>
          </Tr>
          </ContextMenu>
        ))}
      </tbody>
    </Table>
  );
}

function titleFor(a: Approval, t: TFunction<"approvals">): string {
  const name = a.driver_info?.full_name ?? a.summary ?? `#${a.id}`;
  return `${t(`kind.${a.kind}`).toLowerCase()}: ${name}`;
}

function subtitleFor(a: Approval, t: TFunction<"approvals">): string {
  const parts: string[] = [];
  if (a.kind === "hire") {
    const job = payloadString(a.payload, "job") || a.driver_info?.current_job;
    if (job) parts.push(job);
  } else if (a.kind === "transfer") {
    parts.push(`${payloadString(a.payload, "from_owner") || t("pool")} → ${payloadString(a.payload, "to_user")}`);
  } else if (a.summary) {
    parts.push(a.summary);
  }
  if (a.requested_by) parts.push(t("markedBy", { who: a.requested_by.display_name }));
  return parts.join(" · ");
}

function ApproveModal({ approval, busy, onClose, onSubmit }: { approval: Approval | null; busy: boolean; onClose: () => void; onSubmit: (note: string, fee: string) => Promise<void> }) {
  const { t } = useTranslation("approvals");
  const [note, setNote] = useState("");
  const [fee, setFee] = useState("");
  useEffect(() => {
    if (!approval) return;
    setNote("");
    // Carrier to'lovi: ariza summasi, bo'lmasa carrier standarti -- tasdiqlovchi shu yerda tasdiqlaydi yoki o'zgartiradi
    setFee(approval.fee?.amount ?? approval.fee?.carrier_default ?? "");
  }, [approval]);
  return (
    <Modal
      open={approval !== null}
      onOpenChange={(o) => {
        if (!o) {
          setNote("");
          onClose();
        }
      }}
      title={approval ? t("approve.title", { what: titleFor(approval, t) }) : t("common:actions.approve")}
      description={approval ? subtitleFor(approval, t) : undefined}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t("common:actions.cancel")}
          </Button>
          <Button variant="primary" loading={busy} onClick={() => onSubmit(note, fee).then(() => setNote(""))}>
            {t("common:actions.approve")}
          </Button>
        </>
      }
    >
      {approval?.kind === "hire" && (
        <p className="mb-3 text-[12px] text-ink-soft">{t("approve.hireHint")}</p>
      )}
      {approval?.kind === "hire" && approval.fee && (
        <Field label={t("approve.fee", { carrier: approval.fee.carrier })} hint={t("approve.feeHint")} htmlFor="approve-fee" className="mb-3">
          <NumberInput id="approve-fee" min={0} step="1" value={fee} onChange={(e) => setFee(e.target.value)} className="max-w-[200px]" placeholder="0" />
        </Field>
      )}
      <Field label={t("approve.note")} htmlFor="approve-note">
        <Textarea id="approve-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("approve.notePlaceholder")} />
      </Field>
    </Modal>
  );
}

interface RejectInput {
  reason: string;
  note: string;
  return_to?: string;
}

function RejectModal({ approval, reasons, busy, onClose, onSubmit }: { approval: Approval | null; reasons: string[]; busy: boolean; onClose: () => void; onSubmit: (input: RejectInput) => Promise<ApiError | null> }) {
  const { t } = useTranslation("approvals");
  const [reason, setReason] = useState("");
  const [returnTo, setReturnTo] = useState("orientation");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const isHire = approval?.kind === "hire";

  function reset() {
    setReason("");
    setReturnTo("orientation");
    setNote("");
    setErrors({});
  }

  async function submit() {
    if (!reason.trim()) {
      setErrors({ reason: [t("reject.reasonRequired")] });
      return;
    }
    const err = await onSubmit({ reason: reason.trim(), note, return_to: isHire ? returnTo : undefined });
    if (err) setErrors(err.fields ?? { reason: [err.message] });
    else reset();
  }

  return (
    <Modal
      open={approval !== null}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
      title={approval ? t("reject.title", { what: titleFor(approval, t) }) : t("common:actions.reject")}
      description={approval ? subtitleFor(approval, t) : undefined}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t("common:actions.cancel")}
          </Button>
          <Button variant="danger" loading={busy} onClick={submit}>
            {isHire ? t("reject.rejectHire") : t("common:actions.reject")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t("reject.reason")} required error={errors.reason} htmlFor="reject-reason">
          {isHire ? (
            <Select id="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} aria-invalid={!!errors.reason}>
              <option value="">{t("reject.chooseReason")}</option>
              {reasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          ) : (
            <Input id="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("reject.reasonPlaceholder")} aria-invalid={!!errors.reason} />
          )}
        </Field>
        {isHire && (
          <Field label={t("reject.returnTo")} htmlFor="reject-return">
            <Select id="reject-return" value={returnTo} onChange={(e) => setReturnTo(e.target.value)}>
              {RETURN_TO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(`common:status.${o.value}`)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t("reject.comment")} htmlFor="reject-note" error={errors.note}>
          <Textarea id="reject-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("reject.commentPlaceholder")} />
        </Field>
        <p className={cn("text-[11px] text-muted")}>{t("reject.footnote")}</p>
      </div>
    </Modal>
  );
}

export function ApprovalKindBadge({ kind }: { kind: ApprovalKind }) {
  const { t } = useTranslation("approvals");
  return <Badge tone={kind === "expense" || kind === "bonus" ? "orange" : "grey"}>{t(`kind.${kind}`)}</Badge>;
}
